import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Coffee, MapPin, Star } from 'lucide-react';
import { HamburgerMenu } from '../components/HamburgerMenu';
import { NewsletterSignup } from '../components/NewsletterSignup';
import { SEO } from '../components/SEO';
import { ImageSlot } from '../components/ImageSlot';
import { getRoasterBySlug, type Roaster } from '../roasters/roasters';
const SITE_URL = 'https://www.groundedlivermore.com';

/** Brand schema for the roaster, plus a Product entry for their retail bags. */
function buildRoasterSchema(roaster: Roaster) {
  const brand = {
    '@type': 'Brand',
    '@id': `${SITE_URL}/roasters/${roaster.slug}#brand`,
    name: roaster.name,
    description: roaster.cardSummary,
    url: roaster.brandUrl
  };
  return {
    '@context': 'https://schema.org',
    '@graph': [brand, {
      '@type': 'Product',
      name: `${roaster.name} whole bean coffee`,
      description: roaster.whyWeFeatureThem,
      category: 'Coffee',
      brand: {
        '@id': `${SITE_URL}/roasters/${roaster.slug}#brand`
      },
      offers: {
        '@type': 'Offer',
        availability: 'https://schema.org/InStock',
        availableAtOrFrom: {
          '@type': 'CafeOrCoffeeShop',
          name: 'Grounded — Coffee and Local Delights',
          '@id': `${SITE_URL}/#cafe`
        }
      }
    }]
  };
}
export function RoasterProfile() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const roaster = slug ? getRoasterBySlug(slug) : undefined;
  if (!roaster) {
    return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-751" data-mp="6lv5cigmacm4">
        <SEO title="Roaster Not Found | Grounded Livermore" description="This roaster profile could not be found. Meet the California roasters we feature at Grounded in Livermore, CA." canonical={`${SITE_URL}/roasters`} data-id="element-752" data-mp="1mqulne1o6to" />
        <HamburgerMenu data-id="element-753" data-mp="3qfyma9wq5c1l" />

        <main className="pt-40 pb-32 px-6 text-center max-w-xl mx-auto" data-id="element-754" data-mp="3qfov4izy0yub">
          <h1 className="font-bebas text-5xl md:text-6xl mb-6" data-id="element-755" data-mp="3v6f2g1rxjbrn">
            We Couldn't Find That Roaster
          </h1>
          <p className="font-inter text-lg text-white/60 mb-10 leading-relaxed" data-id="element-756" data-mp="3gik8yj63l3di">
            It may have moved, or the link might have a typo. Have a look at the
            roasters we're pouring now.
          </p>
          <Link to="/roasters" className="inline-flex items-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors" data-id="element-757" data-mp="d2dhdb0yiwl3">
            <ArrowLeft size={20} aria-hidden="true" data-id="element-758" data-mp="32crt7chvk8g" />
            SEE ALL ROASTERS
          </Link>
        </main>
      </div>;
  }
  return <div className="min-h-screen bg-[#0a0a0a] text-white selection:bg-[#D97E3A] selection:text-white" data-id="element-759" data-mp="2ntdatlak2fss">
      <SEO title={roaster.metaTitle} description={roaster.metaDescription} canonical={`${SITE_URL}/roasters/${roaster.slug}`} structuredData={buildRoasterSchema(roaster)} data-id="element-760" data-mp="2sryexvsxhjmi" />
      <HamburgerMenu data-id="element-761" data-mp="15w09b8kq671q" />

      <main data-id="element-762" data-mp="15up1mxo5sf5c">
        <article className="pt-32 pb-20 px-6 md:px-12 lg:px-24 max-w-3xl mx-auto" data-id="element-763" data-mp="15uyxh1vvxgiy">
          <motion.div initial={{
          opacity: 0,
          y: 24
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.7,
          ease: 'easeOut'
        }} data-id="element-764" data-mp="1jtyk63zu7lch">
            <Link to="/roasters" className="inline-flex items-center gap-2 font-inter text-sm text-white/40 hover:text-[#D97E3A] transition-colors mb-10" data-id="element-765" data-mp="s3alqzpjsu2i">
              <ArrowLeft size={16} aria-hidden="true" data-id="element-766" data-mp="10wniydunahue" />
              Our Roasters
            </Link>

            <div className="flex items-center gap-2 text-[#D97E3A] font-inter text-xs font-medium uppercase tracking-widest mb-5" data-id="element-767" data-mp="w5f7zddo2of4">
              <MapPin size={14} aria-hidden="true" data-id="element-768" data-mp="8e9vn86xqe9k" />
              {roaster.location}
            </div>

            <h1 className="font-bebas text-5xl md:text-7xl leading-[0.92] mb-10" data-id="element-769" data-mp="4e972uq4gmos">
              {roaster.heading}
            </h1>

            <ImageSlot src={roaster.image} alt={roaster.imageAlt} label={`${roaster.name} photo`} aspect="aspect-[16/9]" fit="contain" className="mb-12" data-id="element-770" data-mp="m2titmuut9tn" />

            {/* Story */}
            <p className="font-inter text-lg text-white/70 leading-relaxed" data-id="element-771" data-mp="vuprfrukgic4">
              {roaster.story}
            </p>

            {/* Why we feature them */}
            <section aria-labelledby="why-heading" className="mt-14 border-l-2 border-white/15 pl-6" data-id="element-772" data-mp="14s01j9vne1fg">
              <h2 id="why-heading" className="font-bebas text-3xl md:text-4xl tracking-wide mb-4" data-id="element-773" data-mp="19ql5nke0t596">
                Why We Feature Them
              </h2>
              <p className="font-inter text-lg text-white/70 leading-relaxed" data-id="element-774" data-mp="3bm3rdfq4b3fq">
                {roaster.whyWeFeatureThem}
              </p>
            </section>

            {/* Currently featuring — the easy-to-update callout */}
            <section aria-labelledby="featuring-heading" className="mt-14 border border-[#D97E3A]/40 bg-[#D97E3A]/[0.07] p-8" data-id="element-775" data-mp="348cpth3tol0i">
              <div className="flex items-center gap-2 mb-4" data-id="element-776" data-mp="2uaghex29s463">
                <Star className="text-[#D97E3A]" size={18} strokeWidth={2} aria-hidden="true" data-id="element-777" data-mp="2ubzzmt7hkpai" />
                <h2 id="featuring-heading" className="font-bebas text-2xl tracking-widest text-[#D97E3A]" data-id="element-778" data-mp="3n05gv5zbzw5l">
                  CURRENTLY FEATURING
                </h2>
              </div>
              <p className="font-inter text-lg text-white/85 leading-relaxed" data-id="element-779" data-mp="3d30xtf0s8gt0">
                {roaster.currentlyFeaturing}
              </p>
            </section>

            {/* Links out */}
            <div className="mt-12 flex flex-col sm:flex-row gap-4" data-id="element-780" data-mp="21nvq1wlsns2j">
              <Link to="/menu" className="inline-flex items-center justify-center gap-3 bg-[#D97E3A] text-white px-8 py-4 font-bebas text-xl tracking-wide hover:bg-[#c06b2e] transition-colors" data-id="element-781" data-mp="tnnapjpt7esa">
                <Coffee size={20} aria-hidden="true" data-id="element-782" data-mp="jqij34d72r43" />
                SEE OUR MENU
              </Link>
              <a href={roaster.brandUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-3 border border-white text-white px-8 py-4 font-bebas text-xl tracking-wide hover:border-[#D97E3A] hover:text-[#D97E3A] transition-colors" data-id="element-783" data-mp="9ycve6tz6vup">
                VISIT {roaster.name.toUpperCase()}
                <ArrowUpRight size={20} aria-hidden="true" data-id="element-784" data-mp="3n54trpoy1dh4" />
              </a>
            </div>
          </motion.div>
        </article>

        <NewsletterSignup data-id="element-785" data-mp="3s2wlailoexks" />

        <footer className="border-t border-white/10 py-12 text-center" data-id="element-786" data-mp="3s36ai3ywd0pf">
          <p className="font-bebas text-2xl text-white/50" data-id="element-787" data-mp="38ixpqi9j5ahl">
            © {new Date().getFullYear()} GROUNDED LIVERMORE
          </p>
        </footer>
      </main>
    </div>;
}